import React from 'react';
import styled from 'styled-components';
import {
    BadgesGrid,
    BadgeSlots,
    BadgesTopLeft,
    Positions,
    QuantityBadge,
    SaleBadge,
} from '@westwing/ui-kit/ProductBadges';
import { useBellaSelector } from 'Client/redux/hooks';
import useFeatureToggle from 'Client/hooks/useFeatureToggle';
import { ProductDetail } from 'Client/redux/productdetail/types';
import { FeatureToggles, ProductInterface } from 'AppShell/appshell_types';
import { ProductSalesType } from 'Client/components/Product/SaleBadge';

const BadgesLayout = styled(BadgesGrid)`
    grid-area: image;
    z-index: 1;
`;

const StyledSaleBadge = styled(SaleBadge)`
    margin-left: 0;
`;

export interface BadgesProps {
    product: ProductDetail;
}

type BadgeColours = { [key in keyof typeof ProductSalesType]: string };

const isProductDiscounted = (product: ProductInterface, featureToggles: FeatureToggles): boolean | undefined => {
    if (featureToggles) {
        const { blackFriday, cyberMonday } = product;
        const isBlackFriday = blackFriday && featureToggles.isBlackFridayActive;
        const isCyberMonday = cyberMonday && featureToggles.isCyberMondayActive;
        return (isBlackFriday || isCyberMonday) && featureToggles.isSpecialSalesEnabled;
    }
    return false;
};

// eslint-disable-next-line complexity
const AddOnProductTileBadges = ({ product }: { product: ProductInterface }) => {
    const configurations = useBellaSelector(({ configurations }) => configurations);
    const { isImageServiceProductImageEnabled, specialSales } = configurations;
    const featureToggles = useFeatureToggle();
    const isDiscounted = isProductDiscounted(product, featureToggles);
    const { blackFriday, cyberMonday } = product;
    const isBlackFriday = blackFriday && featureToggles.isBlackFridayActive;
    const isCyberMonday = cyberMonday && featureToggles.isCyberMondayActive;
    let productSalesType = ProductSalesType.NO_SALES;
    const defaultBadgeSlots: Required<BadgeSlots> = {
        bottomleft: [],
        bottomright: [],
        topleft: [],
        topright: [],
        center: [],
    };

    if (isDiscounted) {
        if (isBlackFriday) {
            productSalesType = ProductSalesType.BLACK_FRIDAY;
        }
        if (isCyberMonday && !isBlackFriday) {
            productSalesType = ProductSalesType.CYBER_MONDAY;
        }
        const colours: BadgeColours = {
            BLACK_FRIDAY: specialSales?.blackFridayBadgeColor || '',
            CYBER_MONDAY: specialSales?.cyberMondayBadgeColor || '',
            NO_SALES: '',
        };
        defaultBadgeSlots[Positions.topleft].push(
            <StyledSaleBadge
                key="sale-badge"
                data-testid="sale-badge"
                color={colours[productSalesType || 'NO_SALES']}
                data-sales-type={productSalesType}
                size="small"
            />
        );
    }

    if (isImageServiceProductImageEnabled && product.quantityBadge && product.quantityBadge > 0 && defaultBadgeSlots) {
        defaultBadgeSlots[Positions.topleft].push(
            <QuantityBadge
                key="quantity-badge"
                data-testid="quantity-badge"
                quantity={product.quantityBadge}
                size="small"
            />
        );
    }

    return (
        <BadgesLayout>
            {defaultBadgeSlots[Positions.topleft].length > 0 && (
                <BadgesTopLeft id="top-left">{defaultBadgeSlots[Positions.topleft]}</BadgesTopLeft>
            )}
        </BadgesLayout>
    );
};

export default AddOnProductTileBadges;
